import { dom } from '@omegagrid/core';
import { FormContainer } from '@omegagrid/form';
import {
	AppComponent, CodeEditor, GridEditor, ParsedComponentId, SqlCompileView, SqlEditor,
	SqlEditorContainer, Application, SyncGridEditorAdapter,
	SqlEditorEvent, SqlEditorQueryPlan, SqlEditorResult, SqlQueryPlan,
} from '@reporting/rp-app-host';
import { File, Parameter, Thread } from '@reporting/rp-types/etl';
import {
	EtlSyncEditorAdapter, FileEditorAdapter, FileFormAdapter, FileMetadataEditorAdapter, FilesGridEditorAdapter,
	ParamSqlEditorAdapter, ThreadApiLinkFormAdapter, ThreadFormAdapter, ThreadsGridEditorAdapter } from './adapters';
import { ParamFormAdapter } from './adapters/paramFormAdapter';
import { ThreadEditorAdapter } from './adapters/threadEditorAdapter';
import { ThreadSqlEditorAdapter } from './adapters/threadSqlEditorAdapter';
import { ThreadSqlParameterListAdapter } from './adapters/threadSqlParameterListAdapter';
import { MergeEditor, Sidebar, SidebarContainer } from './components';
import rp from '@reporting/rp-app-host';

export const ETL_SIDEBAR_ID = 'etl-sidebar';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-mf-etl-sidebar');
}

const createMainSidebarContainer = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SidebarContainer>('rp-mf-etl-sidebar-container');
}

const createThreadForm = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<FormContainer>('og-form-container');
	const adapter = new ThreadFormAdapter();
	adapter.id = componentId.ids[1] ? parseInt(componentId.ids[1]) : null;
	adapter.duplicate = componentId.ids[2] === 'copy';
	component.adapter = adapter;
	component.addEventListener('form.save', () => (rp.host.components.get(ETL_SIDEBAR_ID) as Sidebar)?.refresh('threads'));
	return component as unknown as AppComponent;
}

const createParamForm = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<FormContainer>('og-form-container', {
		adapter: new ParamFormAdapter(
			componentId.ids[1] ? parseInt(componentId.ids[1]) : null,
			componentId.ids[2] ? parseInt(componentId.ids[2]) : null
		)
	}) as unknown as AppComponent;
	//component.addEventListener('form.save', () => (app.host.components.get('etl') as Sidebar)?.refresh());
}

const createThreadApiLinkForm = async (app: Application, componentId: ParsedComponentId) => {
	const id = parseInt(componentId.ids[1]);
	const thread = await app.container.ms('etl').get<Thread>(`threads/${id}`, {
		fields: ['id_thread', 'description', 'accounting'],
		scope: 1
	});
	if (!thread) return null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new ThreadApiLinkFormAdapter(thread);
	return component as unknown as AppComponent;
}

const createFileForm = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<FormContainer>('og-form-container');
	const adapter = new FileFormAdapter(app.container.ms('etl'));
	adapter.idFile = componentId.ids[1] ? parseInt(componentId.ids[1]) : null;
	component.adapter = adapter;
	component.addEventListener('form.save', () => (app.host.components.get(ETL_SIDEBAR_ID) as Sidebar)?.refresh('files'));
	return component as unknown as AppComponent;
}

const createCodeEditor = (app: Application, componentId: ParsedComponentId, thread: Thread) => {
	const component = dom.createElement<CodeEditor>('rp-editor-code');
	component.adapter = new ThreadEditorAdapter(
		app.container.ms('etl'),
		thread
	);
	return component;
}

const createSqlEditorBeforeExecHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	return () => {
		const resultId = `${componentId.type}-threadResult-${componentId.ids[1]}`;
		if (app.host.components.has(resultId)) {
			(app.host.components.components.get(resultId) as SqlEditorResult).clear();
		}
	}
}

const createSqlEditorResultHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	// datasets and messages in stream order - a warning belongs in front of the dataset it preceded
	const results: SqlEditorEvent[] = [];
	const plans: SqlQueryPlan[] = [];
	let resultPromise: Promise<SqlEditorResult> = null;
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');

	return async (e: SqlEditorEvent) => {
		if (e.dataset || e.message) results.push(e);
		if (e.queryPlan) plans.push(e.queryPlan);

		if (e.dataset || e.message) {
			const resultId = `${componentId.type}-threadResult-${componentId.ids[1]}`;
			const openOptions = {
				title: `${component.name ?? ''} [result]`.trimStart(),
				color: tabColor,
				persistent: false
			};
			let editorResult = app.host.components?.components.get(resultId) as SqlEditorResult;
			if (editorResult) {
				app.host.open(resultId, openOptions);
			} else {
				if (!resultPromise || !app.host.components?.has(resultId)) {
					resultPromise = app.host.open(resultId, openOptions) as Promise<SqlEditorResult>;
				}
				editorResult = await resultPromise;
			}
			if (!editorResult) return;

			const adapter = (component as any).editorAdapter ?? (component as any).adapter;
			editorResult.onExport = (format, datasetCount) => adapter?.export(format, datasetCount);

			let result: SqlEditorEvent;
			while (result = results.shift()) {
				if (result.dataset) {
					if (result.dataset.index == 0) editorResult.clear();
					editorResult.addDataset(result.dataset);
				} else {
					editorResult.addMessage(result.message);
				}
			}
		}

		if (e.queryPlan) {
			const resultId = `${componentId.type}-threadQueryPlan-${componentId.ids[1]}`;
			const editorQueryPlan = (await app.host.open(resultId, {
				title: `${component.name} [plan]`,
				color: tabColor,
				persistent: false
			})) as SqlEditorQueryPlan;
			let plan: SqlQueryPlan;
			while (plan = plans.shift()) {
				if (plan.index == 0) editorQueryPlan.clear();
				editorQueryPlan.addPlan(plan);
			}
		}
	}
}

const createSqlCompileView = (component: SqlEditorContainer, e: SqlEditorEvent) => {
	const view = dom.createElement<SqlCompileView>('rp-editor-sql-compile', {
			sql: e.sql,
			offset: e.offset,
			tail: e.tail
	});
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');
	rp.host.open(null, {
		component: view,
		title: `${component.name ?? ''} [compile]`.trimStart(),
		color: tabColor,
		persistent: false
	});
}

const createSqlEditor = (app: Application, componentId: ParsedComponentId) => {
	const thread: Thread = {id_thread: 0, description: 'New thread'};
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	component.adapter = new ThreadSqlEditorAdapter(app.container.ms('etl'), thread);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result',  createSqlEditorResultHandler(component, app, componentId));
	return component;
}

const createThreadSqlEditor = (app: Application, componentId: ParsedComponentId, thread?: Thread) => {
	thread = thread || {id_thread: 0, description: 'New thread', accounting: rp.accountingProvider.a};
	const component = dom.createElement<SqlEditorContainer>('rp-editor-sql-container');
	component.editorAdapter = new ThreadSqlEditorAdapter(
		app.container.ms('etl'),
		thread,
		() => component.parameterList?.getParameterValues()
	);
	component.parameterListAdapter = new ThreadSqlParameterListAdapter(app.container.ms('etl'), thread);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result', createSqlEditorResultHandler(component, app, componentId));
	component.addEventListener('sqleditor.compile', (e: SqlEditorEvent) => createSqlCompileView(component, e));
	return component;
}

const createParameterSqlEditor = async (app: Application, componentId: ParsedComponentId, type: 'content'|'value') => {
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	const client = app.container.ms('etl');
	const parameter = await client.get<Parameter>(`threads/${parseInt(componentId.ids[1])}/parameters/${componentId.ids[2]}`);
	component.adapter = new ParamSqlEditorAdapter(app.container.ms('etl'), parameter, type);
	component.addEventListener('sqleditor.result',  createSqlEditorResultHandler(component, app, componentId));
	return component;
}

const createParameterContentSqlEditor = async (app: Application, componentId: ParsedComponentId) => createParameterSqlEditor(app, componentId, 'content');
const createParameterValueSqlEditor = async (app: Application, componentId: ParsedComponentId) => createParameterSqlEditor(app, componentId, 'value');

const createThreadEditor = async (app: Application, componentId: ParsedComponentId) => {
	const id = parseInt(componentId.ids[1] || "0");
	const thread = id != 0 ? await app.container.ms('etl').get<Thread>(`threads/${id}`, {
		fields: ['id_thread', 'description'],
		scope: 1
	}) : {
		id_thread: 0,
		description: 'New thread'
	};

	if (!thread) return null;

	const res = /\.([a-z]*)/i.exec(thread.description);
	if (res && res[1] && ['json', 'yaml'].includes(res[1].toLowerCase())) {
		return createCodeEditor(app, componentId, thread);
	} else {
		return createThreadSqlEditor(app, componentId, thread);
	}
}

const createSqlEditorResult = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-result');
}

const createSqlEditorQueryPlan = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-plan');
}

const createThreadGridEditor = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new ThreadsGridEditorAdapter()
	});
}

const createFileGridEditor = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new FilesGridEditorAdapter(app.host, app.container.ms('etl'))
	});
}

const createFileEditor = async (app: Application, componentId: ParsedComponentId) => {
	const file = await app.container.ms('etl').get<File>(`files/${componentId.ids[1]}`, {
		fields: ['id_file', 'input_file']
	});
	if (!file) return null;
	const component = dom.createElement<CodeEditor>('rp-editor-code');
	component.adapter = new FileEditorAdapter(
		app.container.ms('etl'),
		app.container.router.accounting,
		file
	);
	return component;
}

const createFileMetadataEditor = async (app: Application, componentId: ParsedComponentId) => {
	const file = await app.container.ms('etl').get<File>(`files/${componentId.ids[1]}`, {
		fields: ['id_file', 'input_file', 'metadata_definition', 'exists']
	});

	return file ? dom.createElement<CodeEditor>('rp-editor-code', {
		adapter: new FileMetadataEditorAdapter(file)
	}) : null;
}

const createThreadBrowser = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new FilesGridEditorAdapter(app.host, app.container.ms('etl'))
	});
}

const createSyncEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SyncGridEditorAdapter(new EtlSyncEditorAdapter());
	return component;
}

const createMergeEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<MergeEditor>('rp-mf-etl-merge-editor');
	component.kind = componentId.ids[1] === 'file' ? 'file' : 'thread';
	component.addEventListener('merge', () => (app.host.components.get(ETL_SIDEBAR_ID) as Sidebar)?.refresh(component.kind === 'file' ? 'files' : 'threads'));
	return component;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['etl', createMainSidebarContainer],
	['sidebar', createMainSidebar],
	['sqlEditor', createSqlEditor],
	['thread', createThreadEditor],
	['threadForm', createThreadForm],
	['threadApiLinkForm', createThreadApiLinkForm],
	['threadResult', createSqlEditorResult],
	['threadQueryPlan', createSqlEditorQueryPlan],
	['threadBrowser', createThreadBrowser],
	['threads', createThreadGridEditor],
	['files', createFileGridEditor],
	['file', createFileEditor],
	['fileMetadata', createFileMetadataEditor],
	['fileForm', createFileForm],
	['parameterForm', createParamForm],
	['parameterContent', createParameterContentSqlEditor],
	['parameterValue', createParameterValueSqlEditor],
	['sync', createSyncEditor],
	['merge', createMergeEditor],
]);
